import IsDev from "@/constant/IsDev";
import ShopSettingDTO from "@/dto/shop/ShopSettingDTO";
import AppVersion from "@/enum/AppVersion";
import ShopType from "@/enum/ShopType";
import GlobalStore from "@/store";

/**
 * 获取订阅续费跳转链接
 */
export function getSubscribeUrlByShopType(shopType: ShopType) {
    const search = new URLSearchParams();

    switch (shopType) {
        case ShopType.TAOBAO:
        case ShopType.TAOBAO_DISTRIBUTION:
        case ShopType.PINDUODUO:
        case ShopType.ALIBABA:
        case ShopType.JD:
        case ShopType.JD_PURCHASE:
        case ShopType.DOUDIAN:
        case ShopType.DOUDIAN_IOP:
        case ShopType.KWAI:
        case ShopType.DOUDIAN_SUPPLIER:
        case ShopType.KWAI_DROPSHIP:
        case ShopType.CHANNEL_SHOP:
        case ShopType.CHANNEL_SHOP_DROPSHIP:
        case ShopType.CHANNEL_SHOP_SUPPLIER:
            if (shopType === ShopType.TAOBAO_DISTRIBUTION) {
                shopType = ShopType.TAOBAO;
            }

            const pageName = getRenewPageName();
            const loginShop = GlobalStore.shopMapping.get(GlobalStore.currentUser?.shopId);

            search.set("renewShopType", shopType);
            search.set("opShopName", loginShop?.name || "");
            search.set("opShopSellerUsername", loginShop?.sellerUsername || "");
            search.set("opShopType", loginShop?.type || "");
            search.set("pageName", pageName);

            return IsDev
                ? `https://v2test.fahuoyi.com/subscription/renewal/confirm-v2?${search.toString()}`
                : `https://v2.fahuoyi.com/subscription/renewal/confirm-v2?${search.toString()}`;

        case ShopType.WEIMOB:
        case ShopType.WEIMOB_EC:
            return "http://fuwu.weimob.com/product_666.html";

        case ShopType.YOUZAN:
            return "https://app.youzanyun.com/cloud-app-detail/41822";

        case ShopType.SUNING:
            return "https://fuwu.suning.com/detail/10002164.html";

        default:
            return null;
    }
}

/**
 * 收集数据使用
 * 判断用户在哪个页面点击续费
 */
function getRenewPageName() {
    const pathname = location.pathname;

    let pageName = "其他";

    if (pathname === "/") {
        pageName = "首页";
    } else if (pathname.startsWith("/order/list")) {
        pageName = "订单列表";
    } else if (pathname.startsWith("/order/search")) {
        pageName = "快速查单";
    } else if (pathname.startsWith("/setting/shop")) {
        pageName = "店铺管理";
    } else if (pathname.startsWith("/subscription/renewal")) {
        pageName = "续费";
    }

    return pageName;
}

/**
 * 获取订购链接
 */
export function getSubscribeUrlByShopSetting(shopSetting: ShopSettingDTO) {
    if (!shopSetting) return null;

    const shopType = shopSetting.type;

    const search = new URLSearchParams();

    switch (shopType) {
        case ShopType.TAOBAO:
        case ShopType.TAOBAO_DISTRIBUTION:

        case ShopType.ALIBABA:
        case ShopType.JD:
        case ShopType.JD_PURCHASE:
        case ShopType.DOUDIAN:
        case ShopType.DOUDIAN_IOP:
        case ShopType.KWAI:
        case ShopType.DOUDIAN_SUPPLIER:
        case ShopType.KWAI_DROPSHIP:
        case ShopType.CHANNEL_SHOP:
        case ShopType.CHANNEL_SHOP_DROPSHIP:
        case ShopType.CHANNEL_SHOP_SUPPLIER:
        case ShopType.PINDUODUO:
            const pageName = getRenewPageName();

            const loginShop = GlobalStore.shopMapping.get(GlobalStore.currentUser?.shopId);

            search.set("renewShopId", String(shopSetting.id));
            search.set("renewShopName", shopSetting.name);
            search.set("renewShopSellerUsername", shopSetting.sellerUsername);
            search.set("renewShopType", shopSetting.type);
            search.set("renewAppVersion", shopSetting.appVersion);
            search.set("opShopName", loginShop?.name || "");
            search.set("opShopSellerUsername", loginShop?.sellerUsername || "");
            search.set("opShopType", loginShop?.type || "");
            search.set("pageName", pageName);

            return IsDev
                ? `https://v2test.fahuoyi.com/subscription/renewal/confirm-v2?${search.toString()}`
                : `https://v2.fahuoyi.com/subscription/renewal/confirm-v2?${search.toString()}`;

        case ShopType.WEIMOB:
        case ShopType.WEIMOB_EC:
            return "http://fuwu.weimob.com/product_666.html";

        case ShopType.YOUZAN:
            return "https://app.youzanyun.com/cloud-app-detail/41822";

        case ShopType.SUNING:
            return "https://fuwu.suning.com/detail/10002164.html";

        default:
            return null;
    }
}

/**
 * 需要联系客服去升级的店铺类型
 * 1.微信小店:防止平台发现软件内的增值服务，由admin中生成
 */
const upgradePremiumNeedServicesShopTypes = [
    ShopType.CHANNEL_SHOP,
    ShopType.CHANNEL_SHOP_DROPSHIP,
    ShopType.CHANNEL_SHOP_SUPPLIER,
];

export function getIsUpgradePremiumNeedServicesShopTypes(shopType: ShopType) {
    return upgradePremiumNeedServicesShopTypes.includes(shopType);
}

/**
 * 升级高级版后需要退款标准版的店铺类型
 */
const upgradeNeedRefundShopTypes = [ShopType.PINDUODUO];

export function getIsUpgradeNeedRefundShopTypes(shopType: ShopType) {
    return upgradeNeedRefundShopTypes.includes(shopType);
}

/**
 * 支持平台支付升级的店铺类型
 */
const supportPlatformPremiumSubscribeShopTypes = [
    ShopType.TAOBAO,
    ShopType.TAOBAO_DISTRIBUTION,
    ShopType.DOUDIAN,
    ShopType.DOUDIAN_IOP,
    ShopType.DOUDIAN_SUPPLIER,
    ShopType.ALIBABA,
    ShopType.PINDUODUO,
    ShopType.JD,
    ShopType.JD_PURCHASE,
];

export function getIsSupportPlatformPremiumSubscribe(shopType: ShopType) {
    return supportPlatformPremiumSubscribeShopTypes.includes(shopType);
}

/**
 * 获取高级版订购链接
 */
export function getPremiumSubscribeUrl(shopType: ShopType) {
    switch (shopType) {
        case ShopType.TAOBAO:
        case ShopType.TAOBAO_DISTRIBUTION:
            return "https://fuwu.taobao.com/ser/detail.htm?service_code=ts-13000&tracelog=search&itemCode=ts-13000-v6";
        case ShopType.DOUDIAN:
            return "https://fuwu.jinritemai.com/detail?service_id=4&from=isv.detail";
        case ShopType.DOUDIAN_IOP:
            return "https://fuwu.jinritemai.com/detail?service_id=20486&from=isv.detail";
        case ShopType.DOUDIAN_SUPPLIER:
            return "https://fuwu.jinritemai.com/detail?service_id=23136&from=isv.detail";
        case ShopType.ALIBABA:
            return "https://pc.1688.com/product/detail.htm?productCode=2GnZC%2F7vOkIn0%2FYV%2FUzI1TJ%2Bj5p7sJz%2F0Br7m5KWMBU%3D&productType=GROUP";
        case ShopType.CHANNEL_SHOP:
            return "https://store.weixin.qq.com/shop/servicemarket/detail?serviceId=2647097328130359296";
        case ShopType.CHANNEL_SHOP_DROPSHIP:
        case ShopType.CHANNEL_SHOP_SUPPLIER:
            return "https://store.weixin.qq.com/supplier/servicemarket/detail?serviceId=4151733152181911569";
        case ShopType.PINDUODUO:
            return "https://fuwu.pinduoduo.com/service-market/service-detail?detailId=169";
        case ShopType.JD:
        case ShopType.JD_PURCHASE:
            return "https://fw.jd.com/main/detail/FW_GOODS-23004?p=fw&t=searchbutton";

        default:
            return null;
    }
}

/**
 * 获取平台的通用订购链接
 */
export function getSubscribeUrl(shopType: ShopType) {
    switch (shopType) {
        case ShopType.TAOBAO: {
            return "https://fuwu.taobao.com/ser/detail.htm?service_code=ts-13000";
        }
        case ShopType.PINDUODUO: {
            return "https://fuwu.pinduoduo.com/service-market/service-detail?detailId=169";
        }
        case ShopType.DOUDIAN: {
            return "https://fuwu.jinritemai.com/detail?service_id=4&from=isv.detail";
        }
        case ShopType.DOUDIAN_IOP: {
            return "https://fuwu.jinritemai.com/detail?service_id=20486&from=isv.detail";
        }
        case ShopType.DOUDIAN_SUPPLIER: {
            return "https://fuwu.jinritemai.com/detail?service_id=23136&from=isv.detail";
        }
        case ShopType.KWAI: {
            return "https://fuwu.kwaixiaodian.com/detail?id=670408179100";
        }
        case ShopType.KWAI_DROPSHIP: {
            return "https://fuwu.kwaixiaodian.com/detail?id=15148390305100";
        }
        case ShopType.ALIBABA: {
            return "https://pc.1688.com/product/detail.htm?productCode=2GnZC%2F7vOkIn0%2FYV%2FUzI1TJ%2Bj5p7sJz%2F0Br7m5KWMBU%3D&productType=GROUP";
        }
        case ShopType.JD:
        case ShopType.JD_PURCHASE: {
            return "https://fw.jd.com/main/detail/FW_GOODS-23004";
        }
        case ShopType.CHANNEL_SHOP: {
            return "https://store.weixin.qq.com/shop/servicemarket/detail?serviceId=2647097328130359296";
        }
        case ShopType.CHANNEL_SHOP_DROPSHIP:
        case ShopType.CHANNEL_SHOP_SUPPLIER: {
            return "https://store.weixin.qq.com/supplier/servicemarket/detail?serviceId=4151733152181911569";
        }
        default:
            break;
    }

    return "";
}
